<template>
  <div class="file-upload-wrapper border rounded p-3 bg-white">
    <div class="row align-items-center g-3">

      <div class="col-12 col-md-7">
        <div class="d-flex align-items-center mb-2">
          <label :for="id" class="form-label fw-bold mb-0 text-dark">{{ label }}</label>
          <i v-if="helpId" class="bi bi-question-circle-fill ms-2 text-muted cursor-pointer"
            @click="$emit('show-help', helpId)" title="Más información"></i>
        </div>

        <div class="input-group">
          <input type="file" @change="handleFileUpload" class="form-control" :id="id" :required="required"
            :accept="accept" />
        </div>

        <div class="mt-2 text-truncate">
          <small class="text-muted">
            <i class="bi bi-paperclip me-1"></i>
            {{ fileName || noFileMessage }}
          </small>
        </div>
      </div>

      <div class="col-12 col-md-5 d-flex justify-content-center">
        <div class="preview-box" :class="{ 'has-file': previewSrc, 'is-pdf': isPdf }">

          <img v-if="previewSrc && isImage" :src="previewSrc" alt="Vista previa" class="preview-content" />

          <video v-else-if="previewSrc && isVideo" controls class="preview-content">
            <source :src="previewSrc" type="video/webm" />
          </video>

          <div v-else-if="previewSrc && isPdf" class="pdf-preview">
            <div class="pdf-icon-wrapper mb-3">
              <i class="bi bi-file-earmark-pdf-fill text-danger" style="font-size: 3.5rem;"></i>
            </div>

            <a :href="previewSrc" target="_blank" class="btn-view-pdf shadow-sm">
              <i class="bi bi-eye-fill me-2"></i>Ver Documento
            </a>
          </div>

          <div v-else class="empty-state text-center text-muted">
            <i class="bi bi-image fs-3 mb-1 d-block opacity-50"></i>
            <span class="small" style="font-size: 0.75rem;">Vista previa</span>
          </div>

        </div>
      </div>
    </div>

    <HelpModal v-if="helpField" :field="helpField" @close="helpField = null" />
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import HelpModal from "@/components/shared/HelpModal.vue";
import { validateFileSize, showFileSizeError, validateImageResolution, showResolutionError } from "@/utils/fileValidation";

export default {
  components: {
    HelpModal,
  },
  props: {
    label: { type: String, required: true },
    id: { type: String, required: true },
    required: { type: Boolean, default: false },
    file: { type: [File, String], default: null },
    preview: { type: [String, File], default: "" },
    noFileMessage: { type: String, default: "Sin selección" },
    accept: { type: String, default: "" },
    helpId: { type: String, default: null },
  },
  emits: ["file-change", "show-help"],
  setup(props, { emit }) {
    const previewSrc = ref(typeof props.preview === 'string' ? props.preview : "");
    const isImage = ref(false);
    const isPdf = ref(false);
    const isVideo = ref(false);
    const helpField = ref(null);

    const MAX_FILE_SIZE_MB = 50;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
    const MAX_WIDTH = 1920;
    const MAX_HEIGHT = 1080;

    watch(
      () => props.preview,
      (newPreview) => {
        if (!newPreview) {
          previewSrc.value = null;
          isImage.value = false;
          isPdf.value = false;
          isVideo.value = false;
        } else if (typeof newPreview === 'string') {
          previewSrc.value = newPreview;
          isImage.value = /\.(jpeg|jpg|gif|png|webp)$/i.test(newPreview) || newPreview.startsWith('data:image');
          isPdf.value = /\.pdf$/i.test(newPreview) || newPreview.startsWith('data:application/pdf');
          isVideo.value = /\.webm$/i.test(newPreview) || newPreview.startsWith('data:video');
        }
      },
      { immediate: true }
    );

    const fileName = computed(() => {
      if (props.file && typeof props.file !== "string") {
        return props.file.name;
      }

      if (typeof props.file === "string") {
        const fullName = props.file.split("/").pop();

        if (fullName.length > 25) {
          const nameStart = fullName.substring(0, 10);
          const nameEnd = fullName.substring(fullName.length - 10, fullName.length);
          return `${nameStart}....${nameEnd}`;
        }
        return fullName;
      }

      return props.noFileMessage || "";
    });

    const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (file) {
        if (!validateFileSize(file, MAX_FILE_SIZE_BYTES)) {
          showFileSizeError(MAX_FILE_SIZE_BYTES);
          event.target.value = "";
          return;
        }

        if (file.type.startsWith("image/")) {
          const isValidResolution = await validateImageResolution(file, MAX_WIDTH, MAX_HEIGHT);
          if (!isValidResolution) {
            showResolutionError(MAX_WIDTH, MAX_HEIGHT);
            event.target.value = "";
            return;
          }
        }

        emit("file-change", file);

        const reader = new FileReader();
        reader.onload = (e) => {
          previewSrc.value = e.target.result;
          isImage.value = file.type.startsWith("image/");
          isPdf.value = file.type === "application/pdf";
          isVideo.value = file.type === "video/webm";
        };
        reader.readAsDataURL(file);
      } else {
        previewSrc.value = "";
        isImage.value = false;
        isPdf.value = false;
        isVideo.value = false;
      }
    };

    return {
      previewSrc,
      handleFileUpload,
      fileName,
      isImage,
      isPdf,
      isVideo,
      helpField
    };
  },
};
</script>

<style scoped>
.preview-box {
  width: 100%;
  height: 160px;
  max-width: 240px;
  border: 2px dashed #e9ecef;
  border-radius: 16px;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.preview-box.has-file {
  border-style: solid;
  border-width: 1px;
  border-color: rgba(0, 0, 0, 0.05);
  background: #ffffff;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.025);
}

.preview-box.has-file:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border-color: #dc3545;
}

.preview-content {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(145deg, #ffffff, #f5f7fa);
}

.pdf-icon-wrapper {
  transition: transform 0.3s ease;
}

.preview-box:hover .pdf-icon-wrapper {
  transform: scale(1.1) rotate(-5deg);
}

.btn-view-pdf {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1.2rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #dc3545;
  background-color: #fff;
  border: 1px solid #dc3545;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  z-index: 10;
}

.btn-view-pdf:hover {
  background-color: #dc3545;
  color: #fff;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

.empty-state {
  color: #adb5bd;
  transition: color 0.3s;
}

.preview-box:hover .empty-state {
  color: #6c757d;
}

.cursor-pointer {
  cursor: pointer;
}
</style>